import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import moment from 'moment';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { Mention, MentionsInput } from 'react-mentions';

import IconSend from '@/assets/ic_send.svg';
import IconSmile from '@/assets/ic_smile.svg';
import { useCommentParagraph } from '@/components/GenericsTournaments/hooks/useCommentParagraph';
import useAuth from '@/hooks/useAuth';
import { useUsersProfilePic } from '@/hooks/useUsersProfilePic';

export type ChatMessage = {
  name: string;
  msg: string;
  time: string;
  profilePic?: string;
};

interface Props {
  handleSubmit: (msg: string) => void;
  isLoading: boolean;
  users: any;
  messages: ChatMessage[];
}

const mentionsStyle = {
  control: {
    backgroundColor: '#fff',
    fontSize: 14,
    fontWeight: 'normal',
  },
  '&singleLine': {
    display: 'block',
    width: '100%',
    highlighter: {
      padding: 0,
      border: 0,
      color: '#000',
      fontSize: 14,
      lineHeight: 'normal',
      fontFamily: 'inherit',
      substring: {
        visibility: 'visible',
      },
    },
    input: {
      padding: 0,
      border: 0,
      margin: 0,
      color: 'transparent',
      caretColor: '#000',
      fontSize: 14,
      lineHeight: 'normal',
      fontFamily: 'inherit',
      backgroundColor: 'transparent',
      boxShadow: 'none',
    },
  },
  suggestions: {
    list: {
      backgroundColor: 'white',
      border: '1px solid rgba(0,0,0,0.15)',
      fontSize: 14,
    },
    item: {
      padding: '5px 15px',
      borderBottom: '1px solid rgba(0,0,0,0.15)',
    },
  },
};

const MESSAGES_INCREMENT_STEP = 5;
const MIN_MESSAGES_TO_SHOW = 5;

export const Chat: React.FC<Props> = memo(
  ({ handleSubmit, users, isLoading, messages }: Props) => {
    const [isShowedTooltip, setIsShowedTooltip] = useState<boolean>(false);
    const [chatMessageValue, setChatMessageValue] = useState<string>('');
    const [messagesList, setMessageList] = useState<ChatMessage[]>([]);
    const [messagesQuantityToShow, setMessagesQuantityToShow] =
      useState<number>(MIN_MESSAGES_TO_SHOW);

    const { setProfilePic } = useUsersProfilePic();

    const isEmptyMessage = chatMessageValue === '';

    useEffect(() => {
      if (messages.length > 0) {
        setMessageList(messages);
      }
    }, [messages]);

    const { Comment } = useCommentParagraph();

    const { userInitialData } = useAuth();

    const onHandleEmoticonSelect = useCallback(
      (emoticonData: { native: string }) => {
        setChatMessageValue((prevState) => prevState + emoticonData.native);
      },
      []
    );

    const convertToFullDate = (input: string): string => {
      const parsedDate = moment(input, 'DD/MM | HH:mm');
      return parsedDate.format('DD/MM/YYYY - HH:mm');
    };

    const onSendMessage = useCallback(() => {
      const now = moment().format('DD/MM | HH:mm');

      // parse de datos a enviar
      const chatObjectToSend: ChatMessage = {
        name: userInitialData?.nombre_dt || '',
        msg: chatMessageValue,
        profilePic:
          userInitialData?.fotoPerfil || userInitialData?.pathImagenPerfil,
        time: now,
      };

      // handle submit
      handleSubmit(chatObjectToSend.msg);

      setMessageList((prevState) => [chatObjectToSend, ...prevState]);
      setChatMessageValue('');
      setIsShowedTooltip(false);
      setMessagesQuantityToShow((prevState) => prevState + 1);
    }, [
      userInitialData?.nombre_dt,
      userInitialData?.fotoPerfil,
      userInitialData?.pathImagenPerfil,
      chatMessageValue,
      handleSubmit,
    ]);

    return (
      <>
        <p
          className="text-center py-5 text-base text-gdt-gray-2 font-bold"
          style={{ boxShadow: '0 2px 50px 0px rgba(0,0,0,.08)' }}
        >
          Escribile a tus amigos
        </p>
        <div className="h-[475px] overflow-y-scroll">
          {isLoading && (
            <div className="flex h-[100%] w-[100%] items-center justify-center">
              Cargando mensajes...
            </div>
          )}
          {messagesList.length === 0 && !isLoading && (
            <div className="flex h-[100%] w-[100%] items-center justify-center text-blue-600 font-bold">
              ¡Enviá el primer mensaje!
            </div>
          )}
          <ul>
            {messagesList
              .slice(0, messagesQuantityToShow)
              .map((message, index) => {
                return (
                  <li
                    key={`${message.name}_${index}`}
                    className="flex justify-center py-[20px] pl-3"
                  >
                    <div className="max-w-[40px] h-[40px]">
                      {setProfilePic({
                        alt: message.name,
                        dtName:
                          message.name.split(' ')[0] +
                          ' ' +
                          message.name.split(' ')[1],
                        url: message.profilePic,
                        size: 40,
                      })}
                    </div>
                    <div className="flex flex-col flex-1 ml-3">
                      <div>
                        <span className="font-bold text-gdt-gray text-[14px]">
                          {message.name}
                        </span>
                        <span className="pl-[10px] text-gdt-gray-4 text-[14px]">
                          {convertToFullDate(message.time)}
                        </span>
                      </div>
                      <div>
                        <Comment comment={message.msg} />
                      </div>
                    </div>
                  </li>
                );
              })}
          </ul>
          {messagesQuantityToShow < messagesList.length && (
            <div
              className="flex justify-center bg-[#e4ebff] text-[#3366ff] hover:cursor-pointer"
              onClick={() =>
                setMessagesQuantityToShow(
                  (prevState) => prevState + MESSAGES_INCREMENT_STEP
                )
              }
            >
              <span className="font-bold p-[2px]">Ver más comentarios</span>
            </div>
          )}
        </div>
        <div
          className="flex h-[70px]"
          style={{ boxShadow: '0 -6px 50px 0px rgba(0,0,0,.08)' }}
        >
          <div className="flex items-center pl-[25px] pr-[10px] hover:cursor-pointer">
            <img
              className="inline"
              height={'28px'}
              src={IconSmile}
              width={'28px'}
              onClick={() => setIsShowedTooltip((prevState) => !prevState)}
            />
          </div>
          <div className="flex items-center flex-[300px] max-w-[300px]">
            <MentionsInput
              allowSuggestionsAboveCursor={true}
              className="mentions"
              maxLength={500}
              placeholder="Escribí tu mensaje..."
              singleLine={true}
              style={mentionsStyle}
              value={chatMessageValue}
              onChange={(event) => setChatMessageValue(event.target.value)}
              onClick={() => setIsShowedTooltip(false)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  if (!isEmptyMessage && chatMessageValue.length <= 500) {
                    onSendMessage();
                  }
                }
              }}
            >
              <Mention
                appendSpaceOnAdd
                data={(query) =>
                  users
                    .filter((user: any) =>
                      user.display.toLowerCase().includes(query.toLowerCase())
                    )
                    .slice(0, 30)
                }
                style={{
                  fontWeight: 'bold',
                  color: '#3366ff',
                }}
                trigger={'@'}
              />
            </MentionsInput>
          </div>
          {!isEmptyMessage && chatMessageValue.length <= 500 && (
            <div
              className="flex items-center flex-[40px] hover:cursor-pointer"
              onClick={onSendMessage}
            >
              <img height={'28px'} src={IconSend} width={'28px'} />
            </div>
          )}
        </div>
        {isShowedTooltip && (
          <div className="absolute">
            <Picker data={data} onEmojiSelect={onHandleEmoticonSelect} />
          </div>
        )}
      </>
    );
  }
);

Chat.displayName = 'Chat';
